import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { theme as antdTheme } from 'antd';

import { apexThemeMode } from '@totem/components/services/optimize/v2/dashboard/chartUtils';
import { MonthlyPerformance } from '@totem/components/services/optimize/v2/dashboard/types';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

type Props = {
  performanceMonths: MonthlyPerformance[];
  height?: number;
};

const RunningTrackerChart = ({ performanceMonths, height = 320 }: Props) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  // Split into actual and projected to render as separate series
  const actualMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'actual',
  );
  const projectedMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'projected',
  );

  // Build cumulative running totals across all months (sorted order)
  let runningTotal = 0;
  const actualCumulative = actualMonths.map((mon) => {
    runningTotal += mon.energyCostSavings + mon.demandCostSavings;
    return {
      x: `${MONTH_NAMES[mon.month - 1]} ${mon.year}`,
      y: parseFloat(runningTotal.toFixed(2)),
    };
  });

  const projectedCumulative = projectedMonths.map((mon) => {
    runningTotal += mon.energyCostSavings + mon.demandCostSavings;
    return {
      x: `${MONTH_NAMES[mon.month - 1]} ${mon.year}`,
      y: parseFloat(runningTotal.toFixed(2)),
    };
  });

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      background: 'transparent',
    },
    theme: { mode: apexThemeMode(token.colorBgBase) },
    stroke: {
      curve: 'smooth',
      width: [3, 2],
      dashArray: [0, 6],
    },
    markers: {
      size: [4, 0],
    },
    xaxis: {
      type: 'category',
      labels: {
        rotate: -45,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      title: { text: 'Cumulative Savings ($)' },
      labels: {
        formatter: (val: number) =>
          `$${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      },
    },
    colors: ['#5B8FF9', '#94D2BD'],
    legend: { position: 'top' },
    tooltip: {
      y: {
        formatter: (val: number) =>
          `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      },
    },
    dataLabels: { enabled: false },
  };

  const series = [
    { name: 'Actual Cumulative Savings', data: actualCumulative },
    { name: 'Projected Cumulative Savings', data: projectedCumulative },
  ];

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="line"
      height={height}
    />
  );
};

export default RunningTrackerChart;
